import React, { useContext, useEffect, useState } from 'react';

import {
  SUBSCRIPTION_PLANS_CONFIG,
  SubscriptionPlan,
} from '@/components/SubscriptionCard/SubscriptionCard.utils';
import { CtaButton } from '@/components/SubscriptionCard/SubscriptionCardContent/CtaButton';
import { Features } from '@/components/SubscriptionCard/SubscriptionCardContent/Features';
import { Info } from '@/components/SubscriptionCard/SubscriptionCardContent/Info';
import { Price } from '@/components/SubscriptionCard/SubscriptionCardContent/Price';
import { EditProfileContext } from '@/contexts/EditProfileProvider';
import { useCurrentUser } from '@/Utils/stripeHooks/useCurrentUser';
import useMediaQuery from '@/Utils/useMediaQuery';

export const SubscriptionCardContent = ({
  plan,
  isCurrentPlan,
  id,
}: {
  plan: SubscriptionPlan;
  isCurrentPlan?: boolean;
  id: number;
}) => {
  const [isFullListVisible, setIsFullListVisible] = useState(false);
  const { selectedBillingFrequency } = useContext(EditProfileContext);

  const isMobile = useMediaQuery('(max-width: 768px)');

  useEffect(() => {
    setIsFullListVisible(!isMobile);
  }, [isMobile]);

  const { data: user } = useCurrentUser();

  if (!user) return null;

  const planConfig = SUBSCRIPTION_PLANS_CONFIG[plan];
  const isSelectedBillingFrequencyYearly =
    selectedBillingFrequency === 'yearly';
  const isFreePlan = plan === 'Free';
  const isFreePlanActive = !user.subscription;
  const userSubscriptionInterval =
    user.subscription?.interval === 'month' ? 'monthly' : 'yearly';
  const showCardBorder = user.subscription
    ? isCurrentPlan && userSubscriptionInterval === selectedBillingFrequency
    : isFreePlan;

  // Free plan has longer content in features, resulting in a larger height. This function magically aligns all cards.
  const getBottomPadding = () => {
    if (isMobile) return '';

    if (isFreePlan) {
      return 'pb-11';
    } else if (showCardBorder && !isFreePlan) {
      return 'pb-4.5rem';
    } else if (isFreePlanActive) {
      return 'pb-5.1rem';
    } else {
      return 'pb-4.85rem';
    }
  };

  return (
    <div className={showCardBorder ? 'px-1 pb-1' : undefined}>
      <div
        className={`flex-col p-8 ${showCardBorder ? 'px-7' : ''} rounded-b-lg bg-white ${getBottomPadding()}`}
      >
        <Price
          price={planConfig.price[selectedBillingFrequency]}
          priceYearlyOriginal={planConfig.price?.yearlyOriginal}
          isSelectedBillingFrequencyYearly={isSelectedBillingFrequencyYearly}
        />

        {isFreePlan && isMobile ? (
          <></>
        ) : isFreePlan ? (
          <div style={{ height: 40, width: '100%' }} />
        ) : (
          <CtaButton plan={plan} isCurrentPlan={isCurrentPlan} id={id} verified={!!user.phone_verified_at}/>
        )}

        <div className='font-serif'>
          <Info
            info={planConfig.info}
            accentColor={planConfig.accentColor}
            plan={plan}
          />

          <Features
            features={
              isFullListVisible
                ? planConfig.features
                : planConfig.features.slice(0, 3)
            }
          />

          {isMobile && (
            <div className='flex gap-2.5 items-center justify-center mt-6'>
              <button
                onClick={() => setIsFullListVisible(!isFullListVisible)}
                className='text-14px text-customBlue font-bold border-none bg-transparent self-center'
              >
                {isFullListVisible ? 'Hide List' : 'See Full List'}
                <img
                  src='/icons/arrow-up.svg'
                  alt=''
                  className={`${isFullListVisible ? undefined : 'rotated'} ml-5`}
                />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
